// src/pages/PaymentSelectPage.js
import React, { memo, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { firebaseApp } from "../firebase/firebaseApp";
import TrendChartDemo from "../components/charts/TrendChartDemo";
import { PriceCard } from "../components/payments/PaymentCard";
import { useUserDoc } from "../hooks/useUserDoc";
import { GlassPanel } from "../components/ui/GlassPanel";

function PaymentSelectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth(firebaseApp);
  const db = getFirestore(firebaseApp);
  const { data } = useUserDoc();

  // Account + plan state from Firestore
  const accountStatus = data?.accountStatus; // "registered" when a proper account exists
  const onboardingStep = data?.onboarding?.step;
  const isRegistered =
    accountStatus === "registered" ||
    onboardingStep === "accountCreated" ||
    onboardingStep === "paymentComplete";

  const currentPlanTier = (
    data?.entitlements?.plan ||
    data?.plan?.tier ||
    ""
  ).toLowerCase(); // "free" | "starter" | "performance" | ""
  const currentInterval =
    data?.plan?.interval || data?.entitlements?.interval || null; // "month" | "year" | null

  // Billing period toggle
  const [period, setPeriod] = useState("monthly"); // "monthly" | "yearly"
  const isMonthly = period === "monthly";
  const cadence = isMonthly ? "/ month" : "/ year";
  const price = (m, y) => (isMonthly ? m : y);

  const selectPlan = useCallback(
    async (planPayload) => {
      const u = auth.currentUser;
      if (!u) {
        navigate("/auth/login", {
          replace: false,
          state: { from: "/payment" },
        });
        return;
      }

      // Only set onboarding.step for non-registered users
      const base = {
        plan: {
          ...planPayload,
          selectedAt: serverTimestamp(),
        },
      };
      const onboardingPatch = isRegistered
        ? {} // ← keep their step (accountCreated/paymentComplete) intact
        : {
            onboarding: { step: "planSelected", updatedAt: serverTimestamp() },
          };

      await setDoc(
        doc(db, "lungform_users", u.uid),
        { ...base, ...onboardingPatch },
        { merge: true }
      );

      // Flow
      if (!isRegistered) {
        if (planPayload.tier === "free") navigate("/auth/create-account");
        else navigate("/pay/checkout");
      } else {
        if (planPayload.tier === "free") navigate("/settings");
        else navigate("/pay/checkout");
      }
    },
    [auth, db, navigate, isRegistered]
  );
  // Helper to mark which tile is "current"
  const isCurrent = useCallback(
    (tier, interval) => {
      // console.log(tier, interval);
      const sameTier = (currentPlanTier || "") === tier;
      // For "free" there is no interval; for paid plans compare interval if we have one
      if (tier === "free") return sameTier;
      if (!sameTier) return false;
      // If we don't know currentInterval (older docs), ignore interval check
      return currentInterval ? currentInterval === interval : true;
    },
    [currentPlanTier, currentInterval]
  );

  return (
    <div className="relative z-10 w-full h-full flex items-start justify-start max-w-5xl p-4 md:p-6 flex-col gap-6">
      {/* Header / Hero */}
      <header className="w-full">
        <h1 className="supertitle-text">
          {isRegistered
            ? t("pay.manageTitle", { defaultValue: "Manage Your Plan" })
            : t("pay.title", { defaultValue: "Choose Your Plan" })}
        </h1>
        <p className="subtitle-text">
          {isRegistered
            ? t("pay.manageSubtitle", {
                defaultValue:
                  "You're signed in. Review your plan and switch at any time.",
              })
            : t("pay.subtitle", {
                defaultValue:
                  "Start free, then upgrade for deeper insights, more tests, and clinician-grade reporting.",
              })}
        </p>
        <div className="mt-4 rounded-3xl overflow-hidden">
          <TrendChartDemo height={180} />
        </div>
      </header>

      {/* Show current plan badge if registered */}
      {/* {isRegistered && currentPlanTier && (
        <div className="w-full rounded-xl bg-greyscale-3/40 border border-white/10 px-4 py-3 text-sm">
          {t("pay.currentPlan", { defaultValue: "Current plan:" })}{" "}
          <span className="font-semibold capitalize">
            {currentPlanTier}
            {currentPlanTier !== "free" && currentInterval
              ? ` · ${currentInterval}`
              : ""}
          </span>
        </div>
      )} */}

      {/* Billing period toggle (still useful for previewing paid tiers) */}
      <div className="w-full flex items-center justify-center">
        <div className="inline-flex rounded-full p-1 shadow bg-greyscale-3">
          <button
            type="button"
            onClick={() => setPeriod("monthly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              isMonthly
                ? "bg-primary text-greyscale-1"
                : "text-white/80 hover:text-white"
            }`}
          >
            {t("pay.toggle.monthly", { defaultValue: "Monthly" })}
          </button>
          <button
            type="button"
            onClick={() => setPeriod("yearly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              !isMonthly
                ? "bg-primary text-greyscale-1"
                : "text-white/80 hover:text-white"
            }`}
          >
            {t("pay.toggle.yearly", { defaultValue: "Yearly (save)" })}
          </button>
        </div>
      </div>

      {/* Plans */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        {/* Free */}
        <PriceCard
          title={t("pay.plans.free.title", { defaultValue: "Free" })}
          price={t("pay.plans.free.price", { defaultValue: "£0" })}
          cadence={t("pay.plans.free.cadence", { defaultValue: "" })}
          choose={() =>
            selectPlan({
              tier: "free",
              interval: null,
              kind: "freemium",
              currency: "GBP",
              price: 0,
              cadenceLabel: "",
              title: "Free",
            })
          }
          ctaLabel={
            isCurrent("free", null)
              ? t("pay.cta.current", { defaultValue: "Current plan" })
              : t("pay.plans.free.cta", {
                  defaultValue: isRegistered ? "Switch to Free" : "Get Started",
                })
          }
          disabled={isCurrent("free", null)}
          features={[
            t("pay.free.1test", { defaultValue: "1 test per month" }),
            t("pay.free.healthscore", { defaultValue: "Basic health score" }),
            t("pay.free.education", { defaultValue: "Educational content" }),
            t("pay.free.community", { defaultValue: "Community access" }),
          ]}
        />

        {/* Starter */}
        {/* <PriceCard
          highlight
          title={t("pay.plans.starter.title", { defaultValue: "Starter" })}
          price={t("pay.plans.starter.price", {
            defaultValue: price("£19.99", "£199"),
          })}
          cadence={t("pay.plans.starter.cadence", { defaultValue: cadence })}
          choose={() =>
            selectPlan({
              tier: "starter",
              interval: isMonthly ? "month" : "year",
              kind: "subscription",
              currency: "GBP",
              price: isMonthly ? 19.99 : 199,
              cadenceLabel: cadence,
              title: "Starter",
            })
          }
          ctaLabel={
            isCurrent("starter", isMonthly ? "month" : "year")
              ? t("pay.cta.current", { defaultValue: "Current plan" })
              : t("pay.plans.starter.cta", {
                  defaultValue: isRegistered
                    ? "Switch to Starter"
                    : "Choose Starter",
                })
          }
          disabled={isCurrent("starter", isMonthly ? "month" : "year")}
          features={[
            t("pay.starter.quick", { defaultValue: "Unlimited quick tests" }),
            t("pay.starter.exert", {
              defaultValue: "4 exertion tests / month",
            }),
            t("pay.starter.trends", { defaultValue: "Basic trends & history" }),
            t("pay.starter.support", { defaultValue: "Email support" }),
          ]}
        /> */}

        {/* Performance */}
        {/* <PriceCard
          title={t("pay.plans.performance.title", {
            defaultValue: "Performance",
          })}
          price={t("pay.plans.performance.price", {
            defaultValue: price("£34.99", "£349"),
          })}
          cadence={t("pay.plans.performance.cadence", {
            defaultValue: cadence,
          })}
          choose={() =>
            selectPlan({
              tier: "performance",
              interval: isMonthly ? "month" : "year",
              kind: "subscription",
              currency: "GBP",
              price: isMonthly ? 34.99 : 349,
              cadenceLabel: cadence,
              title: "Performance",
            })
          }
          ctaLabel={
            isCurrent("performance", isMonthly ? "month" : "year")
              ? t("pay.cta.current", { defaultValue: "Current plan" })
              : t("pay.plans.performance.cta", {
                  defaultValue: isRegistered
                    ? "Switch to Performance"
                    : "Choose Performance",
                })
          }
          disabled={isCurrent("performance", isMonthly ? "month" : "year")}
          features={[
            t("pay.performance.unlimited", {
              defaultValue: "Unlimited all tests",
            }),
            t("pay.performance.metrics", { defaultValue: "Advanced metrics" }),
            t("pay.performance.training", {
              defaultValue: "Training integration",
            }),
            t("pay.performance.insights", { defaultValue: "Weekly insights" }),
            t("pay.performance.techniques", {
              defaultValue: "Breathing techniques",
            }),
          ]}
        /> */}
        <GlassPanel
          className={`relative flex flex-col gap-2 rounded-3xl h-full `}
        >
          <h3 className="subtitle-text">
            {t("pay.exertionReport.title", {
              defaultValue: "Exertion Recovery Report",
            })}
          </h3>
          <p className="text-3xl font-semibold ">
            {t("pay.exertionReport.price", {
              defaultValue: "£5",
            })}
          </p>
          <p className="">
            {t("pay.exertionReport.copy", {
              defaultValue:
                "Request a personalised, clinician-style report for any Exertion Recovery test. One-off purchase, delivered to your email.",
            })}
          </p>
        </GlassPanel>
      </section>

      <p className="muted-text w-full text-center">
        {t("pay.secure", {
          defaultValue: "Secure checkout • Cancel anytime • Yearly saves 17%",
        })}
      </p>
    </div>
  );
}

export default memo(PaymentSelectPage);
